/* eslint-disable react/react-in-jsx-scope */
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import { TreeRoot } from "@/types";
import { FolderOpen } from "lucide-react";
import { FC, useEffect, useMemo, useState } from "react";
import ImageThumbnail from "./ImageThumbnail";
import OfficeThumbnail from "./OfficeThumbnail";
import PdfThumbnail from "./PdfThumbnail";

type DocumentItem = {
  id: number;
  name: string;
  path: string;
  pp: string;
  program: string;
  project: string;
  at?: {
    date?: string;
  };
  createdAt?: string;
};

const getDocumentSubtype = (doc: Record<string, unknown>): number | null => {
  const candidate = doc.subType ?? doc.idSubcategory ?? doc.type;
  const numeric = Number(candidate);
  return Number.isNaN(numeric) ? null : numeric;
};

interface DocumentCardProps {
  data: DocumentItem[];
  typesDocs: Array<{
    label: string;
    value: number | string;
    children?: Array<{ label: string; value: number | string }>;
  }>;
}

const DocumentCard: FC<DocumentCardProps> = ({ data, typesDocs }) => {
  const [openItems, setOpenItems] = useState<string[]>([]);
  const [groupBy, setGroupBy] = useState("chronologie");

  const isImage = (ext: string) =>
    ["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(ext);
  const isOffices = (ext: string) =>
    ["doc", "docx", "xlsx", "ods"].includes(ext);

  const { data: treeFinancer } = useFetchDataV2<TreeRoot[]>(
    `${import.meta.env.VITE_API_ENDPOINT}/tree/financer`,
    `tree-financer`,
    1000 * 60 * 60,
  );

  const subTypeToCategoryMap = useMemo(() => {
    const map = new Map<number, { category: string; subcategory: string }>();
    typesDocs?.forEach((parent) => {
      parent.children?.forEach((child) => {
        map.set(Number(child.value), {
          category: parent.label,
          subcategory: child.label,
        });
      });
    });
    return map;
  }, [typesDocs]);

  const groupedDocuments = useMemo(() => {
    const groups = new Map<
      string,
      { key: string; label: string; date: Date; docs: DocumentItem[] }
    >();

    data?.forEach((doc) => {
      const categoryInfo = subTypeToCategoryMap.get(
        getDocumentSubtype(doc as unknown as Record<string, unknown>) ?? -1,
      );

      if (groupBy === "categorie") {
        const categoryKey = categoryInfo?.category || "Autres";
        const existing = groups.get(categoryKey);
        if (existing) {
          existing.docs.push(doc);
        } else {
          groups.set(categoryKey, {
            key: categoryKey,
            label: categoryKey,
            date: new Date(0),
            docs: [doc],
          });
        }
        return;
      }

      const rawDate = doc.at?.date || doc.createdAt;
      const parsed = rawDate ? new Date(rawDate) : null;

      if (!parsed || Number.isNaN(parsed.getTime())) {
        const undated = groups.get("non-date") || {
          key: "non-date",
          label: "non daté",
          date: new Date(0),
          docs: [],
        };
        undated.docs.push(doc);
        groups.set("non-date", undated);
        return;
      }

      const key = `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, "0")}`;
      const existing = groups.get(key);
      if (existing) {
        existing.docs.push(doc);
      } else {
        groups.set(key, {
          key,
          label: parsed.toLocaleDateString("fr-FR", {
            month: "long",
            year: "numeric",
          }),
          date: new Date(parsed.getFullYear(), parsed.getMonth(), 1),
          docs: [doc],
        });
      }
    });

    return [...groups.values()].sort((a, b) =>
      groupBy === "categorie"
        ? a.label.localeCompare(b.label, "fr")
        : b.date.getTime() - a.date.getTime(),
    );
  }, [data, groupBy, subTypeToCategoryMap]);

  useEffect(() => {
    setOpenItems(groupedDocuments.map((group) => group.key));
  }, [groupedDocuments]);

  return (
    <>
      <div className="mb-4 flex justify-end">
        <Select value={groupBy} onValueChange={setGroupBy}>
          <SelectTrigger className="w-40 border-0 px-4 font-normal text-foreground/90">
            <SelectValue placeholder="Trier par" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value="categorie">Sous-catégorie</SelectItem>
              <SelectItem value="chronologie">Chronologie</SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
      <Accordion
        type="multiple"
        className="w-full"
        value={openItems}
        onValueChange={setOpenItems}
      >
        {groupedDocuments.map((group) => (
          <AccordionItem value={group.key} className="border-0 mb-4" key={group.key}>
            <AccordionTrigger className="py-[10px] hover:no-underline group">
              <div className="flex justify-between items-start w-full bg-[#FFFF] px-[18px] py-[10px] rounded-[9px]">
                <div className="flex items-center gap-2 flex-1 text-left pr-4">
                  <FolderOpen className="text-gray-950" />
                  <div className="flex-1 group-hover:underline text-gray-900">{group.label}</div>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="bg-white -mt-4 pt-8 px-4">
              <div className="flex flex-wrap mt-2 mb-4">
                {group.docs.map((pj, key) => {
                  const extension = pj.name.split(".").pop()?.toLowerCase() || "";
                  const isImg = isImage(extension);
                  const isBlobImage = pj.name?.toLowerCase() === "blob";
                  const isOffice = isOffices(extension);
                  const fileUrl = `${import.meta.env.VITE_IP_API}/media/${pj.path}`;

                  return (
                    <div className="w-full sm:w-1/2 lg:w-1/3 xl:w-1/5 p-2" key={pj.id ?? key}>
                      {isImg || isBlobImage ? (
                        <ImageThumbnail url={fileUrl} treeFinancer={treeFinancer} pj={pj} />
                      ) : extension === "pdf" ? (
                        <PdfThumbnail
                          url={fileUrl}
                          extension={extension}
                          treeFinancer={treeFinancer}
                          pj={pj}
                        />
                      ) : isOffice ? (
                        <OfficeThumbnail
                          srcUrl={fileUrl}
                          extension={extension}
                          treeFinancer={treeFinancer}
                          pj={pj}
                        />
                      ) : (
                        <div className="text-sm text-muted-foreground text-center px-2">
                          Aperçu non disponible
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </>
  );
};

export default DocumentCard;
